import {BusinessModel} from '../../core-logic/models/business/business-model';
import {BehaviorSubject, Observable, of} from 'rxjs';
import {StorageManagerGateway} from '../../core-logic/gateways/app-services/storage-manager-gateway';
import {mapTo, switchMap} from 'rxjs/operators';
import {BusinessUpdateModel} from '../../core-logic/models/business/business-update-model';
import * as _ from 'lodash';
import {isEqual} from 'lodash';
import {UserIdPhoneModel} from '../../core-logic/models/shared/user-id-phone-model';
import {TagModel} from '../../core-logic/models/tag/tag-model';
import {BusinessIdMemberId} from '../../core-logic/models/shared/business-id-member-id';
import {PhoneBusinessIdModel} from '../../core-logic/models/shared/phone-business-id-model';
import {PhoneBusinessIdRoleModel} from '../../core-logic/models/shared/phone-business-id--role-model';
import {BusinessMemberRoleList} from '../enums/business-member-role-list';
import {AddressModel} from '../../core-logic/models/address/address-model';
import {BusinessIdAddressIdArchiveModel} from '../../core-logic/models/shared/business-id-address-id-archive-model';
import {MemberModel} from '../../core-logic/models/business/sub/member/member-model';
import {UserModel} from '../../core-logic/models/user/user-model';
import {TagIdsMinutesBetweenStampsModel} from '../../core-logic/models/tag/tag-ids-minutes-between-stamps-model';

export class StorageBusinessesManagerService {
  private businesses: BusinessModel [] = [];
  private businessesSubject$: BehaviorSubject<BusinessModel[]> = new BehaviorSubject<BusinessModel[]>(this.businesses);
  private idOfLastUpdatedBusiness$: BehaviorSubject<string> = new BehaviorSubject<string>('');

  constructor(private storageManager: StorageManagerGateway) {}

  init() {
    this.storageManager.getCurrentUser()
      .subscribe(currentUser => {
        this.businesses = currentUser.businesses;
        this.emitBusinesses(this.businesses);
        this.businessesSubject$.next(this.businesses);
      });
  }

  getBusinessesSubject$(): Observable<BusinessModel[]> {
    return this.businessesSubject$;
  }

  getBusinessesById(businessId: string): BusinessModel {
    return this.businesses.find(business => business._id === businessId);
  }

  emitBusinesses(businesses: BusinessModel []) {
    this.businessesSubject$.next(businesses);
  }

  addBusiness(business: BusinessModel): Observable<boolean> {
    return this.storageManager.getCurrentUser().pipe(switchMap(userStored => {
      userStored.businesses.push(business);
      this.businesses = userStored.businesses;
      this.emitBusinesses(this.businesses);
      this.emitIdOfLastUpdatedBusiness(business._id);
      return this.storageManager.setCurrentUser(userStored);
    }));
  }

  changeBusinessMemberRole(phoneBusinessIdRoleModel: PhoneBusinessIdRoleModel): Observable<boolean> {
    return this.storageManager.getCurrentUser().pipe(switchMap(currentUser => {
      const currentUserBusinesses: BusinessModel [] = currentUser.businesses;
      const wantedBusiness = currentUserBusinesses.find(business => business._id === phoneBusinessIdRoleModel.business_id);
      const memberIndex = wantedBusiness.members.findIndex(member => _.isEqual(member.user.phone, phoneBusinessIdRoleModel.phone));
      if (memberIndex >= 0) {
        wantedBusiness.members[memberIndex].role = phoneBusinessIdRoleModel.role;
        currentUserBusinesses[currentUserBusinesses.indexOf(wantedBusiness)] = wantedBusiness;
        this.businesses = currentUserBusinesses;
        this.emitBusinesses(this.businesses);
        this.emitIdOfLastUpdatedBusiness(wantedBusiness._id);
        return this.storageManager.setCurrentUser(currentUser);
      }
      return of(false);
    }));
  }

  changeBusinessOwner(businessId: string, userIdPhoneModel: UserIdPhoneModel) {
    return this.storageManager.getCurrentUser().pipe(switchMap(currentUser => {
      const currentUserBusinesses: BusinessModel [] = currentUser.businesses;
      const wantedBusiness = currentUserBusinesses.find(business => business._id === businessId);
      _.remove(wantedBusiness.members, member => _.isEqual(member.user.phone, userIdPhoneModel.phone));
      const administrator = {_id: currentUser._id, phone: currentUser.phone};
      wantedBusiness?.members ? wantedBusiness?.members.push(this.makeMemberModel(administrator, BusinessMemberRoleList.ADMINISTRATOR)) :
        wantedBusiness.members = [this.makeMemberModel(administrator, BusinessMemberRoleList.ADMINISTRATOR)];
      wantedBusiness.owner = userIdPhoneModel;
      currentUserBusinesses[currentUserBusinesses.indexOf(wantedBusiness)] = wantedBusiness;
      this.businesses = currentUserBusinesses;
      this.emitBusinesses(this.businesses);
      this.emitIdOfLastUpdatedBusiness(businessId);
      return this.storageManager.setCurrentUser(currentUser);
    }));
  }

  addBusinessWorker(businessId: string, userIdPhoneModel: UserIdPhoneModel ): Observable<boolean> {
    return this.storageManager.getCurrentUser().pipe(switchMap(currentUser => {
      const currentUserBusinesses: BusinessModel [] = currentUser.businesses;
      currentUserBusinesses.find((wantedBusiness, businessIndex) => {
        if (wantedBusiness._id === businessId ) {
          if (currentUserBusinesses[businessIndex].members) {
            const workerIndex = _.findIndex(currentUserBusinesses[businessIndex].members,
              member => member.user._id === userIdPhoneModel._id);
            workerIndex >= 0 ? currentUserBusinesses[businessIndex].members[workerIndex] = this.makeMemberModel(userIdPhoneModel) :
              currentUserBusinesses[businessIndex].members.push(this.makeMemberModel(userIdPhoneModel));
          } else {
            currentUserBusinesses[businessIndex].members = [this.makeMemberModel(userIdPhoneModel)];
          }
        }
      });
      this.businesses = currentUserBusinesses;
      this.emitBusinesses(this.businesses);
      this.emitIdOfLastUpdatedBusiness(businessId);
      return this.storageManager.setCurrentUser(currentUser);
    }));
  }

  private makeMemberModel(userIdPhoneModel: UserIdPhoneModel , role: BusinessMemberRoleList = BusinessMemberRoleList.WORKER) {
    return {
      user: userIdPhoneModel as UserModel,
      role,
      created_date: new Date(),
    };
  }

  addBusinessAdministrator(businessId: string, userIdPhoneModel: UserIdPhoneModel ): Observable<boolean> {
    return this.storageManager.getCurrentUser().pipe(switchMap(currentUser => {
      const currentUserBusinesses: BusinessModel [] = currentUser.businesses;
      currentUserBusinesses.find((wantedBusiness, businessIndex) => {
        if (wantedBusiness._id === businessId ) {
          if (currentUserBusinesses[businessIndex].members) {
            const administratorIndex = _.findIndex(currentUserBusinesses[businessIndex].members,
              member => member.user._id === userIdPhoneModel._id);
            administratorIndex >= 0 ? currentUserBusinesses[businessIndex].members[administratorIndex] =
                this.makeMemberModel(userIdPhoneModel, BusinessMemberRoleList.ADMINISTRATOR) :
              currentUserBusinesses[businessIndex].members.push(this.makeMemberModel(userIdPhoneModel, BusinessMemberRoleList.ADMINISTRATOR));
          } else {
            currentUserBusinesses[businessIndex].members = [this.makeMemberModel(userIdPhoneModel, BusinessMemberRoleList.ADMINISTRATOR)];
          }
        }
      });
      this.businesses = currentUserBusinesses;
      this.emitBusinesses(this.businesses);
      this.emitIdOfLastUpdatedBusiness(businessId);
      return this.storageManager.setCurrentUser(currentUser);
    }));
  }

  addBusinessManager(businessId: string, userIdPhoneModel: UserIdPhoneModel ): Observable<boolean> {
    return this.storageManager.getCurrentUser().pipe(switchMap(currentUser => {
      const currentUserBusinesses: BusinessModel [] = currentUser.businesses;
      currentUserBusinesses.find((wantedBusiness, businessIndex) => {
        if (wantedBusiness._id === businessId ) {
          if (currentUserBusinesses[businessIndex].members) {
            const managerIndex = _.findIndex(currentUserBusinesses[businessIndex].members,
              member => member.user._id === userIdPhoneModel._id);
            managerIndex >= 0 ? currentUserBusinesses[businessIndex].members[managerIndex] =
                this.makeMemberModel(userIdPhoneModel, BusinessMemberRoleList.MANAGER) :
              currentUserBusinesses[businessIndex].members.push(this.makeMemberModel(userIdPhoneModel, BusinessMemberRoleList.MANAGER));
          } else {
            currentUserBusinesses[businessIndex].members = [this.makeMemberModel(userIdPhoneModel, BusinessMemberRoleList.MANAGER)];
          }
        }
      });
      this.businesses = currentUserBusinesses;
      this.emitBusinesses(this.businesses);
      this.emitIdOfLastUpdatedBusiness(businessId);
      return this.storageManager.setCurrentUser(currentUser);
    }));
  }

  addBusinessAddress(addressModel: AddressModel): Observable<boolean> {
    return this.storageManager.getCurrentUser().pipe(switchMap(currentUser => {
      const currentUserBusinesses = currentUser.businesses as BusinessModel [];
      const wantedBusinessIndex = currentUserBusinesses.findIndex(business => business._id === addressModel.business_id);
      currentUserBusinesses[wantedBusinessIndex].addresses.push(addressModel);
      this.businesses = currentUserBusinesses;
      this.emitBusinesses(this.businesses);
      return this.storageManager.setCurrentUser(currentUser);
    }));
  }

  updateBusinessAddress(previousAddress: AddressModel, updatedAddress: AddressModel): Observable<boolean> {
    return this.storageManager.getCurrentUser().pipe(switchMap(currentUser => {
      const currentUserBusinesses = currentUser.businesses as BusinessModel [];
      const wantedBusinessIndex = currentUserBusinesses.findIndex(business => business._id === previousAddress.business_id);
      if (previousAddress._id === updatedAddress._id) {
        const wantedAddressIndex = currentUserBusinesses[wantedBusinessIndex].addresses
          .findIndex(address => address._id === updatedAddress._id);
        currentUserBusinesses[wantedBusinessIndex].addresses[wantedAddressIndex] = updatedAddress;
      } else {
        _.remove(currentUserBusinesses[wantedBusinessIndex].addresses, address => address._id === previousAddress._id);
        currentUserBusinesses[wantedBusinessIndex].addresses.push(updatedAddress);
      }
      _.uniqBy(currentUserBusinesses[wantedBusinessIndex].addresses, '_id');
      this.businesses = currentUserBusinesses;
      this.emitBusinesses(this.businesses);
      return this.storageManager.setCurrentUser(currentUser);
    }));
  }

  removeBusinessAddress(businessIdAddressIdArchiveModel: BusinessIdAddressIdArchiveModel): Observable<boolean> {
    return this.storageManager.getCurrentUser().pipe(switchMap(currentUser => {
      const currentUserBusinesses = currentUser.businesses as BusinessModel [];
      const wantedBusinessIndex = currentUserBusinesses.findIndex(business => business._id === businessIdAddressIdArchiveModel.business_id);
      _.remove(currentUserBusinesses[wantedBusinessIndex].addresses,
        address => address._id === businessIdAddressIdArchiveModel.address_id_archive.address_id);
      this.businesses = currentUserBusinesses;
      this.emitBusinesses(this.businesses);
      return this.storageManager.setCurrentUser(currentUser);
    }));
  }

  removeBusinessMember(phoneBusinessIdModel: PhoneBusinessIdModel): Observable<boolean> {
    return this.storageManager.getCurrentUser().pipe(switchMap(currentUser => {
      let removed = false;
      const currentUserBusinesses: BusinessModel [] = currentUser.businesses;
      currentUserBusinesses.find((wantedBusiness, wantedBusinessIndex) => {
        if (wantedBusiness._id === phoneBusinessIdModel.business_id) {
          const removedMemberByPhone: MemberModel [] = _.remove(wantedBusiness.members, member =>
            _.isEqual(member.user.phone, phoneBusinessIdModel.phone ));
          if (!_.isEmpty(removedMemberByPhone) ) {
            if (currentUser._id !== wantedBusiness.owner._id) {
              _.remove(currentUserBusinesses, currentUserBusiness => currentUserBusiness._id === wantedBusiness._id);
            }
            removed = true;
          }
        }
      });
      this.businesses = currentUserBusinesses;
      this.emitBusinesses(this.businesses);
      this.emitIdOfLastUpdatedBusiness(phoneBusinessIdModel.business_id);
      return removed ? this.storageManager.setCurrentUser(currentUser) : of(removed);
    }));
  }

  removeBusinessOfRemovedMemberFromSocket(businessIdMemberId: BusinessIdMemberId): Observable<boolean> {
    return this.storageManager.getCurrentUser().pipe(switchMap(currentUser => {
      const currentUserBusinesses: BusinessModel [] = currentUser.businesses;
      _.remove(currentUserBusinesses, businessToRemove => businessToRemove._id === businessIdMemberId.business_id);
      this.businesses = currentUserBusinesses;
      currentUser.businesses = this.businesses;
      this.emitBusinesses(this.businesses);
      this.emitIdOfLastUpdatedBusiness(businessIdMemberId.business_id);
      return this.storageManager.setCurrentUser(currentUser);
    }));
  }

  addBusinessTag(tagModel: TagModel): Observable<string> {
    return this.storageManager.getCurrentUser().pipe(switchMap(currentUser => {
      const currentUserBusinesses: BusinessModel [] = currentUser.businesses;
      currentUserBusinesses.find((wantedBusiness, businessIndex) => {
        if (wantedBusiness._id === tagModel.business ) {
          if (currentUserBusinesses[businessIndex].tags) {
            const tagIndex = _.findIndex(currentUserBusinesses[businessIndex].tags, tag => tag._id === tagModel._id);
            tagIndex >= 0 ? currentUserBusinesses[businessIndex].tags[tagIndex] = tagModel :
              currentUserBusinesses[businessIndex].tags.push(tagModel);
          } else {
            currentUserBusinesses[businessIndex].tags = [tagModel];
          }
        }
      });
      this.businesses = currentUserBusinesses;
      currentUser.businesses = this.businesses;
      this.emitBusinesses(this.businesses);
      return this.storageManager.setCurrentUser(currentUser).pipe(mapTo(tagModel.token));
    }));
  }

  updateBusinessTag(tagModel: TagModel): Observable<boolean> {
    return this.storageManager.getCurrentUser().pipe(switchMap(currentUser => {
      const currentUserBusinesses: BusinessModel [] = currentUser.businesses;
      currentUserBusinesses.forEach((currentUserBusiness, businessIndex) => {
        const wantedTagIndex = currentUserBusiness.tags.findIndex((tag) => tag._id === tagModel._id);
        if (wantedTagIndex >= 0) {
          currentUserBusinesses[businessIndex].tags[wantedTagIndex] = _.merge(currentUserBusinesses[businessIndex].tags[wantedTagIndex], tagModel);
        }
      });
      this.businesses = currentUserBusinesses;
      currentUser.businesses = this.businesses;
      this.emitBusinesses(this.businesses);
      return this.storageManager.setCurrentUser(currentUser);
    }));
  }

  updateTagsMinutesBetweenStamps(businessId: string, tagIdsMinutesBetweenStamps: TagIdsMinutesBetweenStampsModel): Observable<boolean> {
    return this.storageManager.getCurrentUser().pipe(switchMap(currentUser => {
        const currentUserBusinesses: BusinessModel [] = currentUser.businesses;
        const businessIndex = currentUserBusinesses.findIndex(business => business._id === businessId);
        tagIdsMinutesBetweenStamps.tag_ids.forEach(tagId => {
          const tagIndex = currentUserBusinesses[businessIndex].tags.findIndex(tag => tag._id === tagId);
          currentUserBusinesses[businessIndex].tags[tagIndex].minutes_between_stamps = tagIdsMinutesBetweenStamps.minutes_between_stamps;
        });
        this.businesses = currentUserBusinesses;
        currentUser.businesses = this.businesses;
        this.emitBusinesses(this.businesses);
        return this.storageManager.setCurrentUser(currentUser);
      })
    );
  }

  deactivateBusinessTag(tagModel: TagModel): Observable<boolean> {
    return this.storageManager.getCurrentUser().pipe(switchMap(currentUser => {
      const currentUserBusinesses: BusinessModel [] = currentUser.businesses;
      currentUserBusinesses.find((wantedBusiness, indexBusiness) => {
        if (wantedBusiness._id === tagModel.business ) {
          currentUserBusinesses[indexBusiness].tags.find((tag, indexTag) => {
            if (tag._id === tagModel._id) {
              _.unset(currentUserBusinesses[indexBusiness].tags[indexTag], 'activation_date')
            }
          });
        }
      });
      this.businesses = currentUserBusinesses;
      currentUser.businesses = this.businesses;
      this.emitBusinesses(this.businesses);
      return this.storageManager.setCurrentUser(currentUser);
    }));
  }

  removeBusinessTag(tagModel: TagModel): Observable<boolean> {
    return this.storageManager.getCurrentUser().pipe(
      switchMap((currentUser) => {
        let tagRemoved = false;

        const { businesses: currentUserBusinesses } = currentUser;
        const businessToModify = currentUserBusinesses.find((business) => business._id === tagModel.business);

        if (businessToModify) {
          const originalTagCount = businessToModify.tags.length;
          businessToModify.tags = businessToModify.tags.filter((tag) => tag._id !== tagModel._id);
          tagRemoved = businessToModify.tags.length < originalTagCount;
        }

        this.businesses = currentUserBusinesses;
        this.emitBusinesses(this.businesses);
        this.emitIdOfLastUpdatedBusiness(tagModel.business);

        return tagRemoved ? this.storageManager.setCurrentUser(currentUser) : of(false);
      })
    );
  }

  updateBusiness(businessIdAndUpdateModel: { businessId: string; businessUpdateModel: BusinessUpdateModel }): Observable<boolean> {
    return this.storageManager.getCurrentUser().pipe(switchMap(currentUser => {
      const currentUserBusinesses: BusinessModel [] = currentUser.businesses;
      currentUserBusinesses.find((wantedBusiness, index) => {
        if (wantedBusiness._id === businessIdAndUpdateModel.businessId ) {
          if (businessIdAndUpdateModel.businessUpdateModel.rewards) {
            currentUserBusinesses[index].rewards = businessIdAndUpdateModel.businessUpdateModel.rewards;
            delete businessIdAndUpdateModel.businessUpdateModel.rewards;
          }

          if (businessIdAndUpdateModel.businessUpdateModel.addresses) {
            currentUserBusinesses[index].addresses = businessIdAndUpdateModel.businessUpdateModel.addresses;
            delete businessIdAndUpdateModel.businessUpdateModel.addresses;
          }
          currentUserBusinesses[index] = _.merge(wantedBusiness, businessIdAndUpdateModel.businessUpdateModel);
        }
      });
      currentUser.businesses = currentUserBusinesses;
      this.businesses = currentUserBusinesses;
      this.emitBusinesses(this.businesses);
      this.emitIdOfLastUpdatedBusiness(businessIdAndUpdateModel.businessId);
      return this.storageManager.setCurrentUser(currentUser);
    }));
  }

  refreshOrAddBusinessFromSocket(business: BusinessModel): Observable<boolean> {
    return this.storageManager.getCurrentUser().pipe(switchMap(currentUser => {
      const currentUserBusinesses: BusinessModel [] = currentUser.businesses;
      const businessToRefreshIndex: number = _.findIndex(currentUserBusinesses, wantedBusiness => wantedBusiness._id === business._id);
      if (businessToRefreshIndex >= 0 ) {
        if (!this.isSameBusiness(currentUserBusinesses[businessToRefreshIndex], business)) {
          currentUserBusinesses[businessToRefreshIndex] = business;
          this.businesses = currentUserBusinesses;
          this.emitBusinesses(this.businesses);
          this.emitIdOfLastUpdatedBusiness(currentUserBusinesses[businessToRefreshIndex]._id);
          return this.storageManager.setCurrentUser(currentUser);
        } else {
          return of(true);
        }
      } else {
        return this.addBusiness(business);
      }
    }));
  }

  refreshBusinessTagFromSocket(tagModel: TagModel): Observable<boolean> {
    return this.storageManager.getCurrentUser().pipe(switchMap((currentUser) => {
        const { businesses: currentUserBusinesses } = currentUser;

        // Find business and tag indices
        const businessToRefreshIndex = currentUserBusinesses.findIndex((business) => business._id === tagModel.business);
        if (businessToRefreshIndex < 0) { return of(true); }

        const tags = currentUserBusinesses[businessToRefreshIndex]?.tags || [];
        const tagToRefreshIndex = tags.findIndex((tag) => tag._id === tagModel._id);
        if (tagToRefreshIndex < 0) { return of(true); }

        const tagToUpdate = tags[tagToRefreshIndex];
        const fieldsToCompare = ['name', 'token', 'address', 'minutes_between_stamps', 'activation_date'];

        const hasChanges = !isEqual(_.pick(tagToUpdate, fieldsToCompare), _.pick(tagModel, fieldsToCompare));
        if (!hasChanges) { return of(true); }

        currentUserBusinesses[businessToRefreshIndex].tags[tagToRefreshIndex] = tagModel;
        this.businesses = currentUserBusinesses;
        this.emitBusinesses(this.businesses);
        this.emitIdOfLastUpdatedBusiness(currentUserBusinesses[businessToRefreshIndex]._id);
        return this.storageManager.setCurrentUser(currentUser);
      })
    );
  }



  getIdOfLastUpdatedBusiness$(): Observable<string> {
    return this.idOfLastUpdatedBusiness$;
  }

  emitIdOfLastUpdatedBusiness(businessId: string) {
    this.idOfLastUpdatedBusiness$.next(businessId);
  }
  // TODO to remove
  private isSameBusiness = (firstBusiness: BusinessModel, secondBusiness: BusinessModel): boolean =>
    _.isEqual(firstBusiness.members, secondBusiness.members) &&
    _.isEqual(firstBusiness.rewards, secondBusiness.rewards) && _.isEqual(firstBusiness.tags, secondBusiness.tags) &&
    _.isEqual(firstBusiness.customizer, secondBusiness.customizer) && _.isEqual(firstBusiness.addresses, secondBusiness.addresses) &&
    _.isEqual(firstBusiness.name, secondBusiness.name) && _.isEqual(firstBusiness.activity, secondBusiness.activity) &&
    _.isEqual(firstBusiness.loyalty_cards, secondBusiness.loyalty_cards)
    && _.isEqual(firstBusiness?.default_payment_method_id, secondBusiness?.default_payment_method_id)
    && _.isEqual(firstBusiness?.billing_detail, secondBusiness?.billing_detail)
    && _.isEqual(firstBusiness?.suspended_work_times, secondBusiness?.suspended_work_times)
    && _.isEqual(firstBusiness.promotions, secondBusiness.promotions)
    && _.isEqual(firstBusiness.bank_account, secondBusiness.bank_account)
    && _.isEqual(firstBusiness.preferences, secondBusiness.preferences)
    && _.isEqual(firstBusiness.sponsorship, secondBusiness.sponsorship)
}
